import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const API_URL = process.env.REACT_APP_API_URL || '';

const styles = {
  tabs: {
    display: 'flex',
    gap: '10px',
    marginBottom: '25px',
    flexWrap: 'wrap'
  },
  tab: {
    padding: '12px 24px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    background: 'white',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s'
  },
  tabActive: {
    borderColor: '#722F37',
    background: '#722F37',
    color: 'white'
  },
  filters: {
    display: 'flex',
    gap: '15px',
    marginBottom: '25px',
    flexWrap: 'wrap'
  },
  select: {
    padding: '10px 15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    minWidth: '200px'
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '25px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    marginBottom: '20px'
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '20px',
    color: '#333'
  },
  dimensionCard: {
    background: '#f8f9fa',
    borderRadius: '10px',
    padding: '20px',
    marginBottom: '15px'
  },
  dimensionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px'
  },
  dimensionName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333'
  },
  dimensionScore: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#722F37'
  },
  questionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: '1px solid #e0e0e0',
    fontSize: '14px'
  },
  distribution: {
    display: 'flex',
    gap: '5px',
    marginTop: '8px'
  },
  distBar: {
    height: '20px',
    borderRadius: '3px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    color: 'white',
    fontWeight: '500'
  },
  openAnswer: {
    background: '#f8f9fa',
    borderRadius: '8px',
    padding: '15px',
    marginBottom: '10px',
    fontSize: '14px',
    lineHeight: '1.6'
  },
  openAnswerMeta: {
    fontSize: '12px',
    color: '#888',
    marginTop: '8px'
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px',
    color: '#888'
  },
  chartContainer: {
    height: '400px',
    position: 'relative'
  },
  exportButton: {
    padding: '10px 20px',
    background: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500'
  }
};

const COLORS = ['#dc3545', '#fd7e14', '#ffc107', '#28a745', '#20c997'];

function AdminReports() {
  const [activeTab, setActiveTab] = useState('dimensions');
  const [departamento, setDepartamento] = useState('all');
  const [tipo, setTipo] = useState('all');
  const [dimensionFilter, setDimensionFilter] = useState('all');
  const [dimensionsData, setDimensionsData] = useState(null);
  const [openData, setOpenData] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [departamentos, setDepartamentos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/survey/structure`)
      .then(res => res.json())
      .then(data => setDepartamentos(data.departamentos));
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };

      try {
        if (activeTab === 'dimensions') {
          const res = await fetch(`${API_URL}/api/admin/reports/dimensions?departamento=${departamento}&tipo=${tipo}`, { headers });
          setDimensionsData(await res.json());
        } else if (activeTab === 'open') {
          const res = await fetch(`${API_URL}/api/admin/reports/open-answers?departamento=${departamento}`, { headers });
          setOpenData(await res.json());
        } else if (activeTab === 'comparison') {
          const res = await fetch(`${API_URL}/api/admin/reports/comparison`, { headers });
          setComparisonData(await res.json());
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [activeTab, departamento, tipo]);

  const exportCSV = async () => {
    const token = localStorage.getItem('adminToken');
    const res = await fetch(`${API_URL}/api/admin/export/csv`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await res.json();

    const csv = [
      ['ID', 'Tipo', 'Departamento', 'Fecha', 'Dimensión', 'Pregunta', 'Valor'].join(','),
      ...data.map(row => [
        row.id,
        row.tipo_encuesta,
        row.departamento || '',
        row.completed_at,
        row.dimension,
        row.pregunta_index + 1,
        row.valor
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'encuesta_clima_resultados.csv';
    a.click();
  };

  const renderDimensions = () => {
    if (!dimensionsData || !dimensionsData.resultados) {
      return <div style={styles.emptyState}>No hay datos disponibles</div>;
    }

    const filteredDimensions = dimensionFilter === 'all'
      ? dimensionsData.dimensiones
      : dimensionsData.dimensiones.filter(d => d.id === dimensionFilter);

    const chartData = {
      labels: filteredDimensions.map(d => d.nombre.substring(0, 20) + '...'),
      datasets: [{
        label: 'Promedio',
        data: filteredDimensions.map(d =>
          dimensionsData.resultados[d.id]?.promedioGeneral?.toFixed(2) || 0
        ),
        backgroundColor: 'rgba(114, 47, 55, 0.8)',
        borderColor: '#722F37',
        borderWidth: 1
      }]
    };

    return (
      <>
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Resumen por Dimensión</h3>
          <div style={styles.chartContainer}>
            <Bar
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                  y: { min: 0, max: 5 }
                }
              }}
            />
          </div>
        </div>

        {filteredDimensions.map(dim => {
          const results = dimensionsData.resultados[dim.id];
          if (!results) return null;

          return (
            <div key={dim.id} style={styles.dimensionCard}>
              <div style={styles.dimensionHeader}>
                <div style={styles.dimensionName}>{dim.nombre}</div>
                <div style={styles.dimensionScore}>
                  {results.promedioGeneral.toFixed(2)} / 5
                </div>
              </div>

              {dim.preguntas.map((pregunta, idx) => {
                const pregResult = results.preguntas.find(p => p.pregunta_index === idx);
                if (!pregResult) return null;

                const total = pregResult.val_1 + pregResult.val_2 + pregResult.val_3 + pregResult.val_4 + pregResult.val_5;

                return (
                  <div key={idx}>
                    <div style={styles.questionRow}>
                      <span style={{ flex: 1 }}>{pregunta}</span>
                      <span style={{ fontWeight: '600', color: '#722F37' }}>
                        {pregResult.promedio.toFixed(2)}
                      </span>
                    </div>
                    <div style={styles.distribution}>
                      {[pregResult.val_1, pregResult.val_2, pregResult.val_3, pregResult.val_4, pregResult.val_5].map((val, i) => (
                        <div
                          key={i}
                          style={{
                            ...styles.distBar,
                            width: `${(val / total) * 100}%`,
                            background: COLORS[i],
                            minWidth: val > 0 ? '25px' : '0'
                          }}
                        >
                          {val > 0 && val}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </>
    );
  };

  const renderOpenAnswers = () => {
    if (!openData || !openData.respuestas || openData.respuestas.length === 0) {
      return <div style={styles.emptyState}>No hay respuestas abiertas disponibles</div>;
    }

    const grouped = {};
    openData.preguntasAbiertas.forEach(sec => {
      grouped[sec.id] = { nombre: sec.nombre, preguntas: sec.preguntas, respuestas: {} };
      sec.preguntas.forEach((_, idx) => {
        grouped[sec.id].respuestas[idx] = [];
      });
    });

    openData.respuestas.forEach(r => {
      if (grouped[r.seccion] && grouped[r.seccion].respuestas[r.pregunta_index]) {
        grouped[r.seccion].respuestas[r.pregunta_index].push(r);
      }
    });

    return Object.entries(grouped).map(([secId, secData]) => (
      <div key={secId} style={styles.card}>
        <h3 style={styles.cardTitle}>{secData.nombre}</h3>
        {secData.preguntas.map((pregunta, idx) => (
          <div key={idx} style={{ marginBottom: '25px' }}>
            <h4 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '15px', color: '#555' }}>
              {pregunta}
            </h4>
            {secData.respuestas[idx].length > 0 ? (
              secData.respuestas[idx].map((resp, rIdx) => (
                <div key={rIdx} style={styles.openAnswer}>
                  {resp.respuesta}
                  <div style={styles.openAnswerMeta}>
                    {resp.departamento && `${resp.departamento} • `}
                    {resp.tipo_encuesta === 'directivo' ? 'Directivo' : 'Colaborador'}
                  </div>
                </div>
              ))
            ) : (
              <div style={{ color: '#888', fontSize: '14px' }}>Sin respuestas</div>
            )}
          </div>
        ))}
      </div>
    ));
  };

  const renderComparison = () => {
    if (!comparisonData || !comparisonData.resultados || comparisonData.resultados.length === 0) {
      return <div style={styles.emptyState}>No hay datos para comparar</div>;
    }

    const departments = [...new Set(comparisonData.resultados.map(r => r.departamento))];
    const dimensions = comparisonData.dimensiones;

    const chartData = {
      labels: dimensions.map(d => d.nombre.substring(0, 15)),
      datasets: departments.slice(0, 5).map((dep, idx) => ({
        label: dep?.substring(0, 20) || 'Sin departamento',
        data: dimensions.map(dim => {
          const match = comparisonData.resultados.find(
            r => r.departamento === dep && r.dimension === dim.id
          );
          return match?.promedio?.toFixed(2) || 0;
        }),
        backgroundColor: `hsla(${idx * 60}, 70%, 50%, 0.7)`,
        borderColor: `hsl(${idx * 60}, 70%, 40%)`,
        borderWidth: 1
      }))
    };

    return (
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Comparativa por Departamento</h3>
        <div style={{ ...styles.chartContainer, height: '500px' }}>
          <Bar
            data={chartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: {
                y: { min: 0, max: 5 }
              }
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <AdminLayout title="Informes">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px', marginBottom: '25px' }}>
        <div style={styles.tabs}>
          <button
            style={{ ...styles.tab, ...(activeTab === 'dimensions' ? styles.tabActive : {}) }}
            onClick={() => setActiveTab('dimensions')}
          >
            Por Dimensiones
          </button>
          <button
            style={{ ...styles.tab, ...(activeTab === 'open' ? styles.tabActive : {}) }}
            onClick={() => setActiveTab('open')}
          >
            Respuestas Abiertas
          </button>
          <button
            style={{ ...styles.tab, ...(activeTab === 'comparison' ? styles.tabActive : {}) }}
            onClick={() => setActiveTab('comparison')}
          >
            Comparativa
          </button>
        </div>
        <button style={styles.exportButton} onClick={exportCSV}>
          Exportar CSV
        </button>
      </div>

      {activeTab !== 'comparison' && (
        <div style={styles.filters}>
          <select
            style={styles.select}
            value={departamento}
            onChange={(e) => setDepartamento(e.target.value)}
          >
            <option value="all">Todos los departamentos</option>
            {departamentos.map(dep => (
              <option key={dep} value={dep}>{dep}</option>
            ))}
          </select>
          {activeTab === 'dimensions' && (
            <>
              <select
                style={styles.select}
                value={tipo}
                onChange={(e) => setTipo(e.target.value)}
              >
                <option value="all">Todos los tipos</option>
                <option value="colaborador">Colaborador</option>
                <option value="directivo">Directivo</option>
              </select>
              <select
                style={styles.select}
                value={dimensionFilter}
                onChange={(e) => setDimensionFilter(e.target.value)}
              >
                <option value="all">Todas las dimensiones</option>
                {dimensionsData && dimensionsData.dimensiones && dimensionsData.dimensiones.map(dim => (
                  <option key={dim.id} value={dim.id}>{dim.nombre}</option>
                ))}
              </select>
            </>
          )}
        </div>
      )}

      {loading ? (
        <div style={styles.emptyState}>Cargando datos...</div>
      ) : (
        <>
          {activeTab === 'dimensions' && renderDimensions()}
          {activeTab === 'open' && renderOpenAnswers()}
          {activeTab === 'comparison' && renderComparison()}
        </>
      )}
    </AdminLayout>
  );
}

export default AdminReports;
